import { captureException } from "@sentry/browser";
import { useNavigate } from "@tanstack/react-router";
import { formatISO, intlFormatDistance } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

import { getWeekRangeFromDate } from "@/components/date-picker/helpers";
import LoadingAnimation from "@/components/loading-animation";
import LoadingOverlay from "@/components/loading-overlay";
import SignupModal from "@/components/signup-modal";
import { DialogTitle } from "@/components/ui/dialog";
import { datetimeToJS } from "@/helpers/parsers";
import { bookingLinkProps } from "@/helpers/url";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import useTrackCartEvent from "@/pages/cart/hooks/use-track-cart-event";
import useUpdateCart, {
  type UpdateCartVariablesType,
} from "@/pages/cart/hooks/use-update-cart";

import { calculateCartItems, canCreateCart } from "./cart-items";
import DayView from "./day-view";
import Filters from "./filters";
import { useAvailability, useLiveTimeslot } from "./graphql";
import type { GameType, LiveTimeslotType, TimeslotType } from "./types";
import WeekView from "./week-view";

export type { LiveTimeslotType } from "./types";

interface TimeslotsProps {
  children?: React.ReactNode;
  className?: string;
  date?: Date;
  game: GameType;
  ignoreTimezone?: boolean;
  initialView?: "day" | "week";
  isModal?: boolean;
  onChange?(timeslot?: LiveTimeslotType): void;
  setLoading?(loading: boolean): void;
  timeslotId?: string;
}

export default function Timeslots({
  children,
  className,
  date,
  game,
  ignoreTimezone = false,
  initialView = "day",
  isModal = false,
  onChange,
  setLoading = () => {
    /* No-op */
  },
  timeslotId,
}: TimeslotsProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(date || new Date());
  const [isWeekView, setIsWeekView] = useState<boolean>(initialView === "week");
  const isInitializedRef = useRef<boolean>(!!date);
  const [timeslots, setTimeslots] = useState<TimeslotType[]>([]);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isLoadingOverlayOpen, setIsLoadingOverlayOpen] = useState(false);

  const { isUser, me, mutationContext } = useAuth();
  const track = useTrackCartEvent();
  const [fetchAvailability, { data, loading }] = useAvailability();
  const [fetchLiveTimeslot] = useLiveTimeslot();
  const [updateCart] = useUpdateCart();
  const navigate = useNavigate();

  // Calculate date range based on selectedDate and view mode
  const dateRange = useMemo(() => {
    if (isWeekView) {
      return getWeekRangeFromDate(selectedDate);
    }
    return { dateGte: selectedDate, dateLte: selectedDate };
  }, [selectedDate, isWeekView]);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
  const handleClick = async (newTimeslotId: string) => {
    if (!game.hasBookings) {
      const url = bookingLinkProps(game);
      if (url) {
        window.open(url.href);
      }
      return;
    }

    if (!isUser) {
      return setIsSignupModalOpen(true);
    }

    if (newTimeslotId === timeslotId) {
      onChange?.();
      return;
    }

    setIsLoadingOverlayOpen(true);

    const { data: liveTimeslot } = await fetchLiveTimeslot({
      variables: { id: newTimeslotId },
    });

    if (
      !(liveTimeslot?.liveTimeslot && canCreateCart(liveTimeslot.liveTimeslot))
    ) {
      setIsLoadingOverlayOpen(false);
      // biome-ignore lint/suspicious/noAlert: legacy
      return alert("Slot not available anymore ;(");
    }

    if (onChange !== undefined) {
      setIsLoadingOverlayOpen(false);
      return onChange(liveTimeslot?.liveTimeslot);
    }

    const inconvenienceFee =
      localStorage.getItem(`booking_inconvenience_fee_${me?.id}`) || "true";
    const allPricingCategories = [
      ...liveTimeslot.liveTimeslot.pricingCategories,
    ];

    // If multiple pricing categories, start with empty items (all quantities at 0)
    // If single pricing category, use default values from calculateCartItems
    let items: { quantity: number; vendorCategoryId: string }[] = [];

    if (allPricingCategories.length === 1) {
      // Single category: use default calculated values
      const cartItems = calculateCartItems(liveTimeslot.liveTimeslot);

      if (cartItems.length === 0) {
        setIsLoadingOverlayOpen(false);

        // biome-ignore lint/suspicious/noAlert: legacy
        return alert("Unable to create cart for this timeslot.");
      }

      items = cartItems;
    } else {
      // Multiple categories: start with all at 0
      items = allPricingCategories.map(({ vendorCategoryId }) => ({
        quantity: 0,
        vendorCategoryId,
      }));
    }

    try {
      const { data } = await updateCart(
        mutationContext<UpdateCartVariablesType>({
          inconvenienceFee: inconvenienceFee === "true",
          items,
          timeslotId: newTimeslotId,
        })
      );
      if (!data?.updateCart.cart) {
        // biome-ignore lint/suspicious/noAlert: legacy
        return alert(
          "Unable to complete booking at this time. Please try again later."
        );
      }

      sessionStorage.setItem(
        `cart/${data?.updateCart.cart.id}`,
        JSON.stringify(data?.updateCart.cart)
      );
      track("Booking Cart Created", data.updateCart.cart);
      navigate({ to: `/cart/${data?.updateCart.cart.id}` });
    } catch (error) {
      captureException(error);
      // biome-ignore lint/suspicious/noAlert: legacy
      alert("Unable to complete booking at this time. Please try again later.");
    }
  };

  const handleViewChange = () => setIsWeekView((prev) => !prev);

  // Effect to fetch data whenever selectedDate or isWeekView changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchAvailability and setLoading are stable
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Calculate date range inside the effect to avoid circular dependencies
        const currentDateRange = isWeekView
          ? getWeekRangeFromDate(selectedDate)
          : { dateGte: selectedDate, dateLte: selectedDate };

        const { data } = await fetchAvailability({
          variables: {
            dateGte: formatISO(currentDateRange.dateGte, {
              representation: "date",
            }),
            dateLte: formatISO(currentDateRange.dateLte, {
              representation: "date",
            }),
            gameId: game.id,
          },
        });

        // If not initialized and we got data, set the earliest slot as selected
        if (!isInitializedRef.current && data?.availability.timeslots?.length) {
          const earliestSlot = data.availability.timeslots.reduce(
            (accum, slot) =>
              datetimeToJS(accum.datetime, ignoreTimezone) >
              datetimeToJS(slot.datetime, ignoreTimezone)
                ? slot
                : accum
          );
          const initialDate = datetimeToJS(
            earliestSlot.datetime,
            ignoreTimezone
          );
          setSelectedDate(initialDate);
          isInitializedRef.current = true;
          return; // This will trigger another fetch with the correct date
        }

        setTimeslots(data?.availability.timeslots || []);
        setLoading(false);
      } catch (_error) {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, isWeekView, game.id, ignoreTimezone]);

  const isSingleDay = !isWeekView;
  const Component = isSingleDay ? DayView : WeekView;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dont worry
  const timeslotsContent = useMemo(() => {
    if (loading) {
      return <LoadingAnimation className="size-12.5" />;
    }

    if (timeslots.length === 0) {
      return <p>Game is not available on this date</p>;
    }

    return (
      <Component
        dateRange={dateRange}
        ignoreTimezone={ignoreTimezone}
        onClick={handleClick}
        timeslotId={timeslotId}
        timeslots={timeslots}
      />
    );
  }, [Component, loading, timeslots, dateRange, ignoreTimezone, isUser, me]);

  if (!(data?.availability.syncedAt || loading)) {
    return null; //<RequestTimes gameId={gameId} {...props} />;
  }

  return (
    <>
      {children}
      <div
        className={cn(
          "md:rounded-[20px] md:p-5",
          !isModal && "md:bg-deep-koamaru-dark",
          className
        )}
      >
        {isModal ? (
          <DialogTitle className="mb-2.5 text-center font-medium text-16 text-white leading-[1.5em] md:mb-4 md:text-18">
            Select time
          </DialogTitle>
        ) : (
          <h6 className="mb-4 font-semibold text-24 text-white leading-[1.2em] md:mb-5.5 md:hidden md:font-medium md:text-22">
            Book experience
          </h6>
        )}
        <Filters
          isWeekView={isWeekView}
          onDateChange={setSelectedDate}
          onViewChange={handleViewChange}
          selectedDate={selectedDate}
        />
        {timeslotsContent}
        {!!data && (
          <p className="mt-4 text-12/5 text-cool-gray">
            Updated{" "}
            {intlFormatDistance(
              datetimeToJS(data?.availability.syncedAt ?? "", false),
              new Date()
            )}
          </p>
        )}
      </div>
      <SignupModal
        onOpenChange={setIsSignupModalOpen}
        open={isSignupModalOpen}
      />
      <LoadingOverlay
        onOpenChange={setIsLoadingOverlayOpen}
        open={isLoadingOverlayOpen}
      />
    </>
  );
}
